import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Gear, Play, ClipboardText } from '@phosphor-icons/react';
import {
  ObjectivePhaseActionType,
  ObjectiveTimePeriodPhaseFragment,
} from '../../../../__generated__/graphql';

const PHASE_IDS = {
  SETUP: 'setup',
  EXECUTION: 'execution',
  REVIEW: 'review',
} as const;

type Props = {
  phase: ObjectiveTimePeriodPhaseFragment;
};

const formatActionType = (
  actionType: ObjectivePhaseActionType,
  t: (key: string) => string,
): string => {
  switch (actionType) {
    case ObjectivePhaseActionType.Allow:
      return t('performanceReview.goals2.phaseActionType.allow');
    case ObjectivePhaseActionType.RequireApproval:
      return t('performanceReview.goals2.phaseActionType.requireApproval');
    case ObjectivePhaseActionType.Deny:
      return t('performanceReview.goals2.phaseActionType.deny');
    default:
      return actionType;
  }
};

export const GoalPhase: React.FC<Props> = ({ phase }) => {
  const { t } = useTranslation();
  const phaseName = phase.name;
  const PhaseIcon = getPhaseIcon(phase.id.toLowerCase());

  const tooltipContent = (
    <div className="text-start">
      <div>
        {t('performanceReview.goals2.phaseTooltip.currentPhase', { phaseName })}
      </div>
      <div className="mt-1">
        {t('performanceReview.goals2.phaseTooltip.createGoals')}:{' '}
        {formatActionType(phase.createGoals, t)}
      </div>
      <div>
        {t('performanceReview.goals2.phaseTooltip.updateGoals')}:{' '}
        {formatActionType(phase.updateGoals, t)}
      </div>
      <div>
        {t('performanceReview.goals2.phaseTooltip.deleteGoals')}:{' '}
        {formatActionType(phase.deleteGoals, t)}
      </div>
      <div>
        {t('performanceReview.goals2.phaseTooltip.updateWeight')}:{' '}
        {formatActionType(phase.updateWeight, t)}
      </div>
      <div>
        {t('performanceReview.goals2.phaseTooltip.updateProgress')}:{' '}
        {formatActionType(phase.updateProgress, t)}
      </div>
    </div>
  );

  return (
    <OverlayTrigger
      overlay={
        <Tooltip id={`goal-phase-tooltip-${phase.id}`}>
          {tooltipContent}
        </Tooltip>
      }
      placement="top">
      <div
        className="d-flex align-items-center gap-1"
        style={{ cursor: 'help' }}>
        <PhaseIcon size={16} />
        <span className="fs-5">{phaseName}</span>
      </div>
    </OverlayTrigger>
  );
};

const getPhaseIcon = (phaseId: string) => {
  switch (phaseId) {
    case PHASE_IDS.SETUP:
      return Gear;
    case PHASE_IDS.EXECUTION:
      return Play;
    case PHASE_IDS.REVIEW:
      return ClipboardText;
    default:
      return Gear;
  }
};
